import axios from 'axios';
import { getAuthToken, setAuthToken } from './authTokenStore';

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function readModeratorApiKeyFromJwt(token) {
  const payload = decodeJwtPayload(token);
  const key = payload?.api_key;
  return typeof key === 'string' && key.trim() ? key.trim() : '';
}

export function readModeratorApiKeyFromToken(token) {
  return readModeratorApiKeyFromJwt(token);
}

/**
 * Resolve moderator client API key for /dataroom/* and /subscribers/* routes.
 * 1. hawk_moderator_api_key in localStorage (set on login)
 * 2. Fallback: api_key claim inside moderator JWT (hawk_admin_token)
 */
export function getModeratorApiKey() {
  try {
    const stored = localStorage.getItem('hawk_moderator_api_key') || '';
    if (stored.trim()) return stored.trim();

    const jwtToken = localStorage.getItem('hawk_admin_token') || '';
    const fromJwt = readModeratorApiKeyFromJwt(jwtToken);
    if (fromJwt) {
      localStorage.setItem('hawk_moderator_api_key', fromJwt);
      return fromJwt;
    }
    return '';
  } catch {
    return '';
  }
}

export function hydrateModeratorApiKeyFromSession() {
  return getModeratorApiKey();
}

export function isModeratorApiKeyAuthError(err) {
  const status = err?.response?.status;
  const detail = String(err?.response?.data?.detail ?? '').toLowerCase();
  return status === 401 || detail.includes('invalid api key') || detail.includes('inactive');
}

export function formatModeratorApiKeyError(err, fallback = 'Request failed. Please try again.') {
  if (isModeratorApiKeyAuthError(err)) {
    return 'Invalid API key or client inactive. Log out and sign in again as moderator.';
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const message = detail.map((item) => item?.msg).filter(Boolean).join('; ');
    if (message) return message;
  }
  return err?.message || fallback;
}

export function setModeratorApiKey(key) {
  try {
    if (key) {
      localStorage.setItem('hawk_moderator_api_key', key);
    } else {
      localStorage.removeItem('hawk_moderator_api_key');
    }
  } catch {
    /* ignore */
  }
}

/**
 * Create axios instance for Admin-authenticated requests.
 * Reads token from authTokenStore (set by AuthContext on login).
 */
export const createAdminApiClient = () => {
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
  });

  apiClient.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        setAuthToken(null);
        localStorage.removeItem('hawk_admin_token');
        localStorage.removeItem('hawk_admin_user');
        window.location.replace('/login');
      }
      return Promise.reject(error);
    },
  );

  return apiClient;
};

/** Singleton for admin operations (e.g. clients CRUD in clientApi). */
export const adminApiClient = createAdminApiClient();

let moderatorApiClientSingleton = null;

/**
 * Axios for moderator (API key) routes: /topics/, /buzzwords/, /subscribers/, etc.
 * Returns a shared instance so interceptors are not registered multiple times.
 */
export function createModeratorApiClient() {
  if (moderatorApiClientSingleton) {
    return moderatorApiClientSingleton;
  }

  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  apiClient.interceptors.request.use(
    (config) => {
      const key = getModeratorApiKey();
      if (key) {
        config.headers.Authorization = `Bearer ${key}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Intentionally NO 401 handling here:
  //   * No redirect — admin session lifecycle is handled by createAdminApiClient.
  //   * No `setModeratorApiKey(null)` — a single endpoint returning 401 (e.g. a
  //     buggy route, a race at page refresh, or an endpoint that expects admin
  //     JWT instead of the moderator key) must not globally invalidate the key
  //     and cascade-fail every other moderator call with 403 "no auth header".
  //   The moderator API key's lifetime is owned exclusively by AuthContext
  //   (set on successful login, cleared on explicit logout).
  // Callers receive the rejected promise and can surface the error in the UI.

  moderatorApiClientSingleton = apiClient;
  return apiClient;
}

/** Preferred import when you do not need a factory — same instance as createModeratorApiClient(). */
export const moderatorApiClient = createModeratorApiClient();
